import { ArrowLeft, MoreHorizontal, Plus, Search, X } from 'lucide-solid';
import {
  createMemo,
  createSignal,
  For,
  onCleanup,
  onMount,
  Show,
  type Accessor,
  type JSX,
} from 'solid-js';
import { Portal } from 'solid-js/web';
import {
  treeKey,
  treeParents,
  visibleTreeRows,
  type SettingsTreeAction,
  type SettingsTreeControl,
  type SettingsTreeChoice,
  type SettingsTreeNode,
  type SettingsTreePlus,
  type SettingsTreeRow,
  type SettingsTreeSource,
} from '../lib/settingsTree';
import type { EffectZoneNames } from '../lib/effectPackages';
import type { SettingsTreeView } from '../state/settingsTreeView';
import { EffectPackageControls } from './EffectPackageControls';
import './SettingsTreeSheet.css';
import { CLASS_NAMES, TEST_IDS, testIdOf } from '../testIds';

const NO_ZONES: EffectZoneNames = new Map();

/**
 * Open the sheet on one item: selected, and expanded one level. Its children keep whatever open state
 * this viewer left them in, which on a fresh page is collapsed (B84, B85).
 */
export function openSettingsTreeOn(view: SettingsTreeView, id: string): void {
  view.select(id);
  view.setOpen(id, true);
}

/** The item's ⋯ menu: every action without an icon, under the console's existing `More actions for` name. */
export function SettingsTreeMenu(props: {
  name: string;
  actions: readonly SettingsTreeAction[];
  control: SettingsTreeControl;
  disabled?: boolean;
}): JSX.Element {
  const [open, setOpen] = createSignal(false);
  const entries = createMemo(() => props.actions.filter((action) => !action.icon));
  return (
    <Show when={entries().length}>
      <div class="scene-menu settings-tree-menu">
        <button
          type="button"
          class="icon-button"
          aria-label={`More actions for ${props.name}`}
          aria-expanded={open()}
          disabled={props.disabled}
          onClick={() => setOpen((current) => !current)}
          onKeyDown={(event) => {
            if (event.key === 'Escape' && open()) {
              event.stopPropagation();
              setOpen(false);
            }
          }}
        ><MoreHorizontal size={18} /></button>
        <Show when={open()}>
          <div class="scene-menu-popover" role="menu">
            <For each={entries()}>
              {(action) => (
                <button
                  type="button"
                  role="menuitem"
                  classList={{ 'danger-text': Boolean(action.danger) }}
                  disabled={action.disabled}
                  onClick={() => {
                    setOpen(false);
                    action.run(props.control);
                  }}
                >{action.label}</button>
              )}
            </For>
          </div>
        </Show>
      </div>
    </Show>
  );
}

/** The selected item: its own control at the head, its actions beside it, then its parameter rows. */
function SettingsTreeDetail(props: { node: SettingsTreeNode; control: SettingsTreeControl }): JSX.Element {
  const icons = createMemo(() => props.node.actions.filter((action) => action.icon));
  return (
    <div class="settings-detail" data-testid={testIdOf('settingsDetail', { node: props.node.id })}>
      <div class="settings-detail-head">
        <div class="settings-detail-name">
          <Show when={props.node.head} fallback={<h3>{props.node.name}</h3>}>
            {(head) => head()()}
          </Show>
        </div>
        <div class="settings-detail-actions">
          <For each={icons()}>
            {(action) => {
              const Icon = action.icon!;
              return (
                <button
                  type="button"
                  class="icon-button"
                  classList={{ 'danger-hover': Boolean(action.danger) }}
                  aria-label={action.label}
                  disabled={action.disabled}
                  onClick={() => action.run(props.control)}
                ><Icon size={16} /></button>
              );
            }}
          </For>
          <SettingsTreeMenu name={props.node.name} actions={props.node.actions} control={props.control} />
        </div>
      </div>
      <Show when={props.node.controls}>{(controls) => controls()()}</Show>
      <Show when={props.node.parameters}>
        {(item) => (
          <EffectPackageControls
            item={item()}
            values={() => props.node.values ?? {}}
            synced={() => props.node.synced === true}
            disabled={() => props.node.disabled}
            idPrefix={props.node.parameterPrefix ?? testIdOf('settingsNode', { node: props.node.id })}
            zoneNames={() => props.node.zoneNames ?? NO_ZONES}
            committing={(key) => props.node.committing?.(key) === true}
            onUpdate={(values, key, final) => props.node.onParameters?.(values, key, final)}
          />
        )}
      </Show>
      <Show when={props.node.add}>{(add) => <div class="settings-detail-add">{add()(props.control)}</div>}</Show>
    </div>
  );
}

/** A choice list in menu order: the ungrouped choices, then each group under its own name. */
function ChoiceOptions(props: { choices: readonly SettingsTreeChoice[] }): JSX.Element {
  const loose = createMemo(() => props.choices.filter((choice) => !choice.group));
  const groups = createMemo(() => [...new Set(props.choices.flatMap((choice) => (choice.group ? [choice.group] : [])))]);
  return (
    <>
      <option value="" />
      <For each={loose()}>{(choice) => <option value={choice.value}>{choice.name}</option>}</For>
      <For each={groups()}>
        {(group) => (
          <optgroup label={group}>
            <For each={props.choices.filter((choice) => choice.group === group)}>
              {(choice) => <option value={choice.value}>{choice.name}</option>}
            </For>
          </optgroup>
        )}
      </For>
    </>
  );
}

/**
 * The plus at the right end of a tree row: the row's own add (B88). A plain add is a button; an add that
 * needs a choice is the add's own menu, drawn as the same plus. It is its own control inside the row:
 * a press or a key on it adds and never chooses the row or moves through the tree.
 */
function TreePlus(props: { id: string; plus: SettingsTreePlus; onAdded: (id: string | undefined) => void }): JSX.Element {
  const stop = (event: Event): void => event.stopPropagation();
  return (
    <Show
      when={props.plus.choices}
      fallback={(
        <button
          type="button"
          class="settings-tree-plus"
          aria-label={props.plus.label}
          disabled={props.plus.disabled}
          data-testid={testIdOf('settingsTreePlus', { node: props.id })}
          onClick={(event) => {
            event.stopPropagation();
            props.onAdded(props.plus.run());
          }}
          onKeyDown={stop}
        ><Plus size={16} /></button>
      )}
    >
      {(choices) => (
        <span class="settings-tree-plus" classList={{ disabled: props.plus.disabled === true }}>
          <Plus size={16} aria-hidden="true" />
          <select
            aria-label={props.plus.label}
            value=""
            disabled={props.plus.disabled}
            data-testid={testIdOf('settingsTreePlus', { node: props.id })}
            onClick={stop}
            onKeyDown={stop}
            onChange={(event) => {
              const choice = event.currentTarget.value;
              // The menu is an action, not a state: it rests again the moment it has been used.
              event.currentTarget.value = '';
              if (choice) props.onAdded(props.plus.run(choice));
            }}
          ><ChoiceOptions choices={choices()} /></select>
        </span>
      )}
    </Show>
  );
}

interface TreeItemProps {
  id: string;
  source: SettingsTreeSource;
  view: SettingsTreeView;
  rows: Accessor<Map<string, SettingsTreeRow>>;
  focused: Accessor<string>;
  register: (id: string, element: HTMLElement | undefined) => void;
  onChoose: (id: string) => void;
  onToggle: (id: string) => void;
  onAdded: (id: string | undefined) => void;
}

/**
 * One row and, while it is open, its children beneath it.
 *
 * The arrow is ALWAYS the row's first cell — a node with no children keeps the cell, empty — so every
 * row at one depth has its arrow in the same column and the next depth's one indent to the right. The
 * plus is always the row's last cell, a column every row reserves whether or not it has one, so no row
 * moves when a row gains or loses its plus (B88).
 */
function TreeItem(props: TreeItemProps): JSX.Element {
  const node = createMemo(() => props.source.node(props.id));
  const row = createMemo(() => props.rows().get(props.id));
  const selected = createMemo(() => props.view.selected() === props.id);
  return (
    <Show when={row() && node()}>
      <div
        ref={(element) => {
          props.register(props.id, element);
          onCleanup(() => props.register(props.id, undefined));
        }}
        class="settings-tree-node"
        role="treeitem"
        aria-level={row()!.depth}
        aria-label={node()!.name}
        aria-selected={selected()}
        aria-expanded={row()!.hasChildren ? row()!.open : undefined}
        tabindex={props.focused() === props.id ? 0 : -1}
        style={{ '--depth': String(row()!.depth) }}
        data-testid={testIdOf('settingsTreeNode', { node: props.id })}
      >
        <div
          class={CLASS_NAMES.settingsTreeRow}
          classList={{ selected: selected() }}
          onClick={() => props.onChoose(props.id)}
        >
          <span
            class="settings-tree-chevron"
            classList={{ open: row()!.open, empty: !row()!.hasChildren }}
            aria-hidden="true"
            data-testid={testIdOf('settingsTreeChevron', { node: props.id })}
            onClick={(event) => {
              if (!row()!.hasChildren) return;
              event.stopPropagation();
              props.onToggle(props.id);
            }}
          />
          <span class="settings-tree-name">{node()!.name}</span>
          <Show when={node()!.tag}><span class="settings-tree-tag">{node()!.tag}</span></Show>
          <Show when={node()!.summary && !row()!.open}>
            <small class="settings-tree-summary">{node()!.summary}</small>
          </Show>
          <Show when={node()!.plus}>
            {(plus) => <TreePlus id={props.id} plus={plus()} onAdded={props.onAdded} />}
          </Show>
        </div>
        <Show when={row()!.open}>
          <div class="settings-tree-group" role="group">
            <For each={node()!.children}>
              {(child) => <TreeItem {...props} id={child} />}
            </For>
          </div>
        </Show>
      </div>
    </Show>
  );
}

export interface SettingsTreeSheetProps {
  title: string;
  source: SettingsTreeSource;
  view: SettingsTreeView;
  open: boolean;
  onClose: () => void;
}

/**
 * THE tree view: every card whose settings are a tree opens this, and nothing in it knows which card
 * it is (S34).
 *
 * Tree on the left, the selected item's parameters on the right in the row every flat card uses. The
 * header is the card's name, one fixed save slot and Close; Escape and a click on the dimmed page close
 * it too. There is no Save or Cancel — every edit is already saved as it is made. On a phone the sheet
 * fills the screen and the item slides over the tree, with Back to return (B84).
 */
export function SettingsTreeSheet(props: SettingsTreeSheetProps): JSX.Element {
  return (
    <Show when={props.open}>
      <Portal>
        <SheetBody {...props} />
      </Portal>
    </Show>
  );
}

function SheetBody(props: SettingsTreeSheetProps): JSX.Element {
  const [query, setQuery] = createSignal('');
  const [pane, setPane] = createSignal<'tree' | 'detail'>(props.view.selected() ? 'detail' : 'tree');
  const [focusedId, setFocusedId] = createSignal(props.view.selected());
  const elements = new Map<string, HTMLElement>();
  let detail: HTMLDivElement | undefined;

  const rows = createMemo(() => visibleTreeRows(props.source.roots(), props.source.node, props.view.open(), query()));
  const rowsById = createMemo(() => new Map(rows().map((row) => [row.id, row])));
  const selectedNode = createMemo(() => props.source.node(props.view.selected()));
  const focused = createMemo(() => (rowsById().has(focusedId()) ? focusedId() : rows()[0]?.id ?? ''));

  function register(id: string, element: HTMLElement | undefined): void {
    if (element) elements.set(id, element);
    else if (elements.get(id)) elements.delete(id);
  }

  function focusRow(id: string): void {
    setFocusedId(id);
    queueMicrotask(() => elements.get(id)?.focus());
  }

  function choose(id: string): void {
    props.view.select(id);
    props.source.onSelect?.(id);
    setPane('detail');
    focusRow(id);
  }

  function toggle(id: string): void {
    props.view.setOpen(id, !props.view.open().has(id));
  }

  /**
   * Select an item for the viewer and show it: every collapsed ancestor opens, so a parent chosen after a
   * remove, or an item just added, is never hidden behind one.
   */
  function reveal(id: string): void {
    props.view.select(id);
    props.source.onSelect?.(id);
    const parents = treeParents(props.source.roots(), props.source.node);
    for (let parent = parents.get(id); parent; parent = parents.get(parent)) props.view.setOpen(parent, true);
    setFocusedId(id);
  }

  /** A new item, from a row's plus or the item's own add: selected at once and shown (B89). */
  function added(id: string | undefined): void {
    if (!id || !props.source.node(id)) return;
    reveal(id);
    setPane('detail');
    // Its row may be past the bottom of the tree's own scroll; nothing else moves.
    queueMicrotask(() => elements.get(id)?.scrollIntoView?.({ block: 'nearest' }));
  }

  const control: SettingsTreeControl = {
    select: reveal,
    added,
    focusHead: () => {
      setPane('detail');
      queueMicrotask(() => {
        const field = detail?.querySelector<HTMLInputElement | HTMLSelectElement>('.settings-detail-name input, .settings-detail-name select');
        field?.focus();
        if (field instanceof HTMLInputElement) field.select();
      });
    },
  };

  function onTreeKey(event: KeyboardEvent): void {
    const result = treeKey(rows(), focused(), event.key);
    if (result.kind === 'none') return;
    event.preventDefault();
    if (result.kind === 'focus') focusRow(result.id);
    else if (result.kind === 'open') props.view.setOpen(result.id, true);
    else if (result.kind === 'close') props.view.setOpen(result.id, false);
    else choose(result.id);
  }

  onMount(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      // A confirmation standing over the sheet answers its own Escape.
      if ((event.target as Element | null)?.closest?.('.modal')) return;
      props.onClose();
    };
    document.addEventListener('keydown', onKey);
    onCleanup(() => document.removeEventListener('keydown', onKey));
    const start = props.view.selected();
    if (start) focusRow(start);
  });

  return (
    <div
      class="settings-sheet-backdrop"
      role="presentation"
      data-testid={TEST_IDS.settingsSheetScrim}
      onMouseDown={(event) => { if (event.target === event.currentTarget) props.onClose(); }}
    >
      <section
        class="settings-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={props.title}
        data-pane={pane()}
        data-testid={TEST_IDS.settingsSheet}
      >
        <header class="settings-sheet-head">
          <button
            type="button"
            class="icon-button settings-sheet-back"
            aria-label="Back"
            data-testid={TEST_IDS.settingsSheetBack}
            onClick={() => setPane('tree')}
          ><ArrowLeft size={18} /></button>
          <h2>{props.title}</h2>
          <span class="settings-sheet-save" aria-live="polite" data-testid={TEST_IDS.settingsSheetSave}>
            <Show when={props.source.saveSlot && props.view.selected()}>
              {props.source.saveSlot!(props.view.selected())}
            </Show>
          </span>
          <button
            type="button"
            class="icon-button settings-sheet-close"
            aria-label="Close"
            data-testid={TEST_IDS.settingsSheetClose}
            onClick={() => props.onClose()}
          ><X size={20} /></button>
        </header>
        <div class="settings-sheet-body">
          <div class="settings-sheet-tree">
            <label class="library-search settings-sheet-search">
              <Search size={17} aria-hidden="true" />
              <input
                type="search"
                value={query()}
                aria-label="Search"
                data-testid={TEST_IDS.settingsTreeSearch}
                onInput={(event) => setQuery(event.currentTarget.value)}
              />
            </label>
            <div
              class="settings-tree"
              role="tree"
              aria-label={props.title}
              data-testid={TEST_IDS.settingsTree}
              onKeyDown={onTreeKey}
            >
              <For each={props.source.roots()}>
                {(id) => (
                  <TreeItem
                    id={id}
                    source={props.source}
                    view={props.view}
                    rows={rowsById}
                    focused={focused}
                    register={register}
                    onChoose={choose}
                    onToggle={toggle}
                    onAdded={added}
                  />
                )}
              </For>
              {/*
                * The tree's own add, when its first level has no row to carry one: the last row of the
                * tree, holding only the plus, in the plus column every row shares (B88).
                */}
              <Show when={props.source.plus}>
                {(plus) => (
                  <div class={`${CLASS_NAMES.settingsTreeRow} settings-tree-add`} role="none" style={{ '--depth': '1' }}>
                    <span class="settings-tree-chevron empty" aria-hidden="true" />
                    <TreePlus id="tree" plus={plus()} onAdded={added} />
                  </div>
                )}
              </Show>
            </div>
          </div>
          <div ref={detail} class="settings-sheet-detail" data-testid={TEST_IDS.settingsSheetDetail}>
            <Show when={props.source.saveDetail && props.view.selected()}>
              {props.source.saveDetail!(props.view.selected())}
            </Show>
            <Show when={selectedNode()} keyed>
              {(node) => <SettingsTreeDetail node={node} control={control} />}
            </Show>
          </div>
        </div>
      </section>
    </div>
  );
}

/**
 * One first-level item as a row of a card's pane list: the same row the tree draws, the arrow cell
 * reserved and empty, and choosing it opens the sheet on that item (B84).
 */
export function SettingsTreeRootRow(props: { node: SettingsTreeNode; onOpen: () => void; testId?: string }): JSX.Element {
  return (
    <button
      type="button"
      class={`${CLASS_NAMES.settingsTreeRow} ${CLASS_NAMES.settingsRootRow}`}
      style={{ '--depth': '1' }}
      data-testid={props.testId}
      onClick={() => props.onOpen()}
    >
      <span class="settings-tree-chevron empty" aria-hidden="true" />
      <span class="settings-tree-name">{props.node.name}</span>
      <Show when={props.node.tag}><span class="settings-tree-tag">{props.node.tag}</span></Show>
    </button>
  );
}
